import type { DashboardPeriod, DashboardTrendDay } from "@estelle/domain";

import { sumMoney } from "@/modules/gst/format";

export interface TrendSummary {
  /** The days the period counts — all of them, or only today for "Today". */
  orders: number;
  /** Their revenue, or null when the viewer may not see money. */
  revenue: string | null;
  /** The bar named on the chart: the busiest day, or today for "Today". */
  focus: number | null;
}

/**
 * What the orders chart says about itself (#638): the same orders the cards
 * count, and which bar to name.
 *
 * For a period of days that is every bar, and the named bar is the busiest
 * (by revenue where it is shown, by orders otherwise; the earliest wins a
 * tie). For "Today" the chart draws the week for context, but the figures
 * and the named bar are today's, so the Orders card and the chart agree.
 */
export function summariseTrend(
  trend: readonly DashboardTrendDay[],
  period: DashboardPeriod,
): TrendSummary {
  const counted = period === "today" ? trend.slice(-1) : trend;
  const hasRevenue = trend.every((day) => day.revenue !== null);
  const orders = counted.reduce((sum, day) => sum + day.orders, 0);
  const revenue = hasRevenue
    ? sumMoney(counted.map((day) => day.revenue ?? "0.00"))
    : null;

  if (trend.length === 0) return { orders, revenue, focus: null };
  if (period === "today") return { orders, revenue, focus: trend.length - 1 };

  const valueOf = (day: DashboardTrendDay) =>
    hasRevenue ? Number(day.revenue) : day.orders;
  let focus = 0;
  trend.forEach((day, index) => {
    if (valueOf(day) > valueOf(trend[focus] ?? day)) focus = index;
  });
  const peak = trend[focus];
  // A period with nothing placed has no busiest day to name.
  return { orders, revenue, focus: peak && valueOf(peak) > 0 ? focus : null };
}
